import React from 'react';
import { StyleSheet, View } from 'react-native';
import { NOT_SERVICEABLE_MESSAGE } from '../../services/location';
import { colors, radii, spacing } from '../../theme';
import ConfirmSheet from '../ui/ConfirmSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

type Props = {
  visible: boolean;
  /**
   * The address that cannot be served, as the line already on screen.
   *
   * Named back to the customer rather than left implicit: someone with three
   * addresses on file needs to know WHICH one this is about, and "we don't
   * cover your area" over an unnamed address is a message they cannot act on.
   */
  address: string;
  /** Opens the address sheet - the one action that can actually fix this. */
  onChangeAddress: () => void;
  onClose: () => void;
};

/**
 * The stop, at the moment of booking.
 *
 * ---------------------------------------------------------------------------
 * WHY A SHEET HERE WHEN TWO BANNERS ALREADY SAY THIS
 * ---------------------------------------------------------------------------
 * `CoverageBanner` on Home and the line under the map in `LocationScreen` both
 * warn, and both are deliberately passive - nothing is blocked, because
 * someone browsing from an uncovered address is still a customer worth having
 * and may well be setting an address for a relative in a city we DO cover.
 *
 * Neither of them stopped anything. An address in a city that is not on
 * `/cities` went through checkout exactly like a covered one: the cart minted
 * an order, payment took it, and the failure surfaced - if at all - as a
 * server rejection with no words a customer could act on. That is the gap this
 * closes. A warning that is never enforced trains people to scroll past it.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A CHOICE AND NOT AN ERROR
 * ---------------------------------------------------------------------------
 * The primary button changes the address, because that is the only thing that
 * can turn a no into a yes, and most people hitting this have another address
 * on file or one they can add. The way out is "Not now" rather than a bare
 * dismiss, so backing out reads as a decision rather than as a failure.
 *
 * Worded as "not yet" throughout - the platform is in a handful of cities and
 * adding more, so this is a waiting list and not a rejection.
 */
function CoverageSheet({ visible, address, onChangeAddress, onClose }: Props) {
  return (
    <ConfirmSheet
      visible={visible}
      icon="pin"
      /* Neutral, not danger. Nothing has gone wrong and nothing was lost - the
         basket is untouched and the address is still perfectly valid, just not
         one we can reach today. Red would read as a rejection of the customer. */
      tone="neutral"
      title="We're coming to your city soon"
      /* The shared half - see `NOT_SERVICEABLE_MESSAGE` - plus the part only
         a blocked CHECKOUT can say. The save path adds a different ending,
         because there the address was refused and here it is only the booking
         that cannot go ahead. */
      body={`${NOT_SERVICEABLE_MESSAGE} We can't send a professional to this address yet. Try another address, or check back soon.`}
      confirmLabel="Use another address"
      cancelLabel="Not now"
      onConfirm={onChangeAddress}
      onClose={onClose}
    >
      {/* The address itself, so the message is about something specific rather
          than about "your area" in the abstract. */}
      <View style={styles.address}>
        <Icon name="pin" size={14} color={colors.inkMuted} />
        <Text
          variant="caption"
          color={colors.inkBody}
          numberOfLines={2}
          style={styles.addressText}
        >
          {address}
        </Text>
      </View>
    </ConfirmSheet>
  );
}

const styles = StyleSheet.create({
  address: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  addressText: {
    flex: 1,
  },
});

export default React.memo(CoverageSheet);
